import { Button } from '@amzn/stencil-react-components/button';
import { Col, Container, Row, Spacer } from '@amzn/stencil-react-components/layout';
import { ModalContent, WithModal } from '@amzn/stencil-react-components/modal';
import { Spinner, SpinnerSize } from '@amzn/stencil-react-components/spinner';
import { Label, Text } from '@amzn/stencil-react-components/text';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { uploadImage } from '../../actions/createContentAction';
import { MAX_ALLOWED_FILE_SIZE } from '../../constants/createSegment';
import { useUserInfo } from '../../hooks/useUserInfo';
import { processImageFile, updateImageUploadStatus } from '../../util/imageFileUtil';
import { IconImage } from '../atoms/custom-icon/IconImage';

const ImageUploader = ({ title, close }) => {
  const dispatch = useDispatch();
  const userInfo = useUserInfo();
  const imageDetails = useSelector(store => store.createContent?.imageDetails);

  const [isLoading, setIsLoading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [height, setHeight] = useState(312);
  const [width, setWidth] = useState(512);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(
    () => {
      updateImageUploadStatus(imageDetails, setIsLoading);
    },
    [imageDetails?.status],
  );

  const resetState = () => {
    setIsLoading(false);
    setIsUploaded(false);
    setHasError(false);
    setHeight(312);
    setWidth(512);
    setIsSubmitted(false);
  };

  const renderModal = ({ close }) => (
    <ModalContent
      titleText={title}
      width={500}
      buttons={[
        <Button
          onClick={() => {
            close();
            resetState();
          }}
          primary
        >
          <FormattedMessage id="app.button.done" defaultMessage="Done" />
        </Button>,
      ]}
    >
      <Container
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        padding={10}
      >
        <Row display="flex" flexDirection="column" gridGap={15}>
          <Col display="flex" flexDirection="column" gridGap={10}>
            <Label for="image-upload">
              <FormattedMessage
                id="app.createContent.label.chooseImage"
                defaultMessage="Choose an image"
              />
            </Label>
            <input
              id="image-upload"
              data-testid="image-upload-input"
              type="file"
              accept={'image/*'}
              onChange={e => onChangeHandler(e)}
            />
            {isLoading &&
              !isUploaded && (
                <Label display="flex">
                  <FormattedMessage
                    id="app.content.createContent.uploadingImage"
                    defaultMessage="Uploading image ..."
                  />
                  <Spacer width={10} />
                  <Spinner size={SpinnerSize.Small} />
                </Label>
              )}
          </Col>
        </Row>
      </Container>
    </ModalContent>
  );

  const onChangeHandler = event => {
    setIsLoading(true);
    setIsSubmitted(true);
    const file = event?.target?.files[0];

    if (file.size > MAX_ALLOWED_FILE_SIZE) {
      handlerError();
      return;
    }

    const dataUrl = processImageFile(file);

    dataUrl
      .then(async data => {
        const mimeTypes = file?.type?.split('/');
        const extension = mimeTypes[mimeTypes.length - 1];
        const payload = {
          fileName: file?.name,
          content: data,
          contentType: file?.type,
          fileSize: String(file?.size),
          fileExtension: extension,
        };
        await dispatch(uploadImage(payload, userInfo.idToken));
        setIsLoading(false);
        setIsUploaded(true);
        setHasError(false);
      })
      .catch(() => {
        handlerError();
      });
  };

  const handlerError = () => {
    setIsLoading(false);
    setHasError(true);
    setIsUploaded(false);
  };

  return (
    <WithModal renderModal={renderModal}>
      {({ open }) => (
        <Row display="flex" justifyContent="stretch">
          <Button
            display="flex"
            flexDirection="column"
            tertiary
            data-testid="image-upload-button"
            onClick={open}
            style={{ borderRadius: '3px', height: '60px', width: '130px' }}
          >
            <IconImage size={20} />
            <Text>{title}</Text>
          </Button>
        </Row>
      )}
    </WithModal>
  );
};

export default ImageUploader;
